import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { FileText, Eye, EyeOff, ArrowRight, Check, Copy, Download, ShieldAlert } from 'lucide-react';

export default function Register() {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [recoveryKey, setRecoveryKey] = useState(null);
  const [copied, setCopied] = useState(false);
  const [formData, setFormData] = useState({
    organizationName: '',
    email: '',
    password: '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    if (!/\d/.test(formData.password)) {
      toast.error('Password must contain at least one number');
      return;
    }
    if (!/[a-zA-Z]/.test(formData.password)) {
      toast.error('Password must contain at least one letter');
      return;
    }

    setLoading(true);

    try {
      const result = await register(formData.email, formData.password, formData.organizationName);
      toast.success('Account created successfully!');
      if (result.recovery_key) {
        setRecoveryKey(result.recovery_key);
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Register error:', error);
      const message = error.response?.data?.detail || 'Failed to create account';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleCopyKey = () => {
    navigator.clipboard.writeText(recoveryKey);
    setCopied(true);
    toast.success('Recovery key copied to clipboard');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownloadKey = () => {
    const content = `RaiseRoot Account Recovery Key\n${'='.repeat(40)}\nEmail: ${formData.email}\nRecovery Key: ${recoveryKey}\n\nKeep this file in a safe place.\nYou will need this key to reset your password if you ever forget it.\nThis key can only be used once — a new one will be issued after each use.`;
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'raiseroot-recovery-key.txt';
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Recovery key downloaded');
  };

  // Recovery key modal after registration
  if (recoveryKey) {
    return (
      <div className="min-h-screen pt-24 pb-16 flex items-center justify-center" data-testid="recovery-key-page">
        <div className="w-full max-w-lg px-4">
          <Card className="bg-white border-amber-200 shadow-card border-2">
            <CardContent className="pt-8 pb-6">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-amber-50 rounded-full flex items-center justify-center mx-auto mb-4">
                  <ShieldAlert className="w-8 h-8 text-amber-600" />
                </div>
                <h2 className="font-heading text-xl font-bold text-primary-900 mb-2">Save Your Recovery Key</h2>
                <p className="text-stone-600 text-sm">
                  This is your <strong>only way</strong> to reset your password if you forget it. 
                  Save it somewhere safe — it will not be shown again.
                </p>
              </div>

              <div className="bg-stone-900 rounded-lg p-4 mb-4 relative group">
                <code className="text-green-400 text-sm font-mono break-all block" data-testid="recovery-key-display">
                  {recoveryKey}
                </code>
              </div>

              <div className="flex gap-3 mb-6">
                <Button
                  onClick={handleCopyKey}
                  variant="outline"
                  className="flex-1 border-stone-300"
                  data-testid="copy-recovery-key-btn"
                >
                  {copied ? <Check className="w-4 h-4 mr-2 text-green-600" /> : <Copy className="w-4 h-4 mr-2" />}
                  {copied ? 'Copied!' : 'Copy'}
                </Button>
                <Button
                  onClick={handleDownloadKey}
                  variant="outline"
                  className="flex-1 border-stone-300"
                  data-testid="download-recovery-key-btn"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download
                </Button>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-6">
                <p className="text-xs text-amber-800 leading-relaxed">
                  <strong>Important:</strong> Store this key in a password manager, printed document, or other secure location. 
                  Without this key, you will not be able to reset your password without contacting support.
                </p>
              </div>

              <Button
                onClick={() => navigate('/dashboard')}
                className="w-full h-11 bg-primary hover:bg-primary-800 text-white"
                data-testid="continue-to-dashboard-btn"
              >
                I've Saved My Key — Continue
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-16 flex items-center justify-center bg-[#FAFAFA]" data-testid="register-page">
      <div className="w-full max-w-md px-4">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 mb-4">
            <div className="w-10 h-10 bg-gradient-to-br from-[#11302A] to-emerald-600 rounded-xl flex items-center justify-center">
              <FileText className="w-5 h-5 text-white" />
            </div>
            <span className="font-heading font-bold text-2xl text-[#0A0A0A]">RaiseRoot</span>
          </Link>
        </div>

        <Card className="bg-white border-[#E2E8E6] shadow-lg rounded-2xl">
          <CardHeader className="text-center pb-2">
            <CardTitle className="font-heading text-2xl text-[#0A0A0A]">Start your 7-day free trial</CardTitle>
            <CardDescription className="text-[#5C6B67]">
              Create your account. Choose a plan on the next screen. No charge for 7 days.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="organizationName" className="text-[#0A0A0A] text-sm font-medium">Organization Name</Label>
                <Input
                  id="organizationName"
                  type="text"
                  placeholder="Your Nonprofit"
                  value={formData.organizationName}
                  onChange={(e) => setFormData({ ...formData, organizationName: e.target.value })}
                  required
                  className="h-11 bg-[#FAFAFA] border-[#E2E8E6] focus:border-[#00A859] rounded-xl"
                  data-testid="register-org"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email" className="text-[#0A0A0A] text-sm font-medium">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@nonprofit.org"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="h-11 bg-[#FAFAFA] border-[#E2E8E6] focus:border-[#00A859] rounded-xl"
                  data-testid="register-email"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-[#0A0A0A] text-sm font-medium">Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Min 8 chars with letters & numbers"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required
                    minLength={8}
                    className="h-11 bg-[#FAFAFA] border-[#E2E8E6] focus:border-[#00A859] pr-10 rounded-xl"
                    data-testid="register-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                className="w-full h-12 bg-[#11302A] hover:bg-[#0D241F] text-white rounded-full font-medium text-base"
                disabled={loading}
                data-testid="register-submit"
              >
                {loading ? (
                  <div className="w-5 h-5 spinner" />
                ) : (
                  <>
                    Create Account
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            </form>

            {/* Benefits */}
            <div className="mt-6 pt-6 border-t border-[#E2E8E6]">
              <p className="text-xs text-[#5C6B67] mb-3 font-medium">What's included in your trial:</p>
              <ul className="space-y-2">
                {[
                  'Full grant search — 25 searches on Starter, 50 on Growth',
                  'Corporate Partnership Builder with smart matching',
                  'Deadline tracker, calendar & watchlist',
                  'Learning hub + community access',
                ].map((benefit) => (
                  <li key={benefit} className="flex items-center gap-2 text-sm text-[#0A0A0A]">
                    <Check className="w-4 h-4 text-[#00A859]" />
                    {benefit}
                  </li>
                ))}
              </ul>
            </div>

            <div className="mt-6 text-center">
              <p className="text-sm text-[#5C6B67]">
                Already have an account?{' '}
                <Link to="/login" className="text-[#00A859] hover:underline font-medium">
                  Log in
                </Link>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
